// "Due by now" — a day's target, pro-rata against the hours a station actually
// works.
//
// Wall-clock pro-rata is the trap the review names: CNC and Howick run a
// two-shift calendar that starts at 6am and runs to 22:00, GRC Cure runs 24/7,
// and everyone else runs a standard week with tea and lunch cut out of it. At
// 09:00 a wall-clock reading tells the CNC night shift it is losing when it has
// not started, and tells GRC Cure it is a third of the way through a day it is
// two thirds through. So the sums here run against `resource.calendar`.
//
// Odoo models breaks as attendance rows with `day_period = 'lunch'`; they are
// excluded before they ever reach this file (see api/client.ts), so a span here
// is productive time and nothing else.

import type { CalendarVM } from '@/api/types';

/** Minutes in a day. Named because the ratio maths reads badly with 1440 in it. */
const DAY_MINUTES = 24 * 60;

export interface PaceWindow {
  /** Productive minutes the calendar schedules for today. */
  totalMinutes: number;
  /** Of those, how many have passed. */
  elapsedMinutes: number;
  /** Today is a working day for this calendar at all. */
  scheduled: boolean;
  /** Now falls inside a productive span (not a break, not before the start). */
  onShift: boolean;
  /** Fraction of the day worked, 0..1. Zero on a day the calendar does not work. */
  fraction: number;
}

/**
 * The local wall-clock time in a named zone, as {dayOfWeek, minutes}.
 *
 * `Intl` rather than date arithmetic: the factory runs in Australia/Sydney,
 * which changes offset twice a year, and a tablet may be set to anything at
 * all. Asking the formatter is the only way to get this right without shipping
 * a timezone database.
 */
export function zonedNow(tz: string, at: Date = new Date()): { dayOfWeek: number; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  // Odoo numbers days from Monday = 0; Intl gives a name, which is stable.
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dayOfWeek = Math.max(0, DAYS.indexOf(get('weekday')));
  // 24:00 is a legal en-GB rendering of midnight; it must read as 0, not 1440.
  const hour = Number(get('hour')) % 24;
  const minutes = hour * 60 + Number(get('minute'));
  return { dayOfWeek, minutes };
}

/**
 * Which half of a two-week calendar today falls in.
 *
 * Odoo's own rule: the ISO week number's parity, where even weeks are the
 * `'0'` type. No calendar on this instance uses it today, so this exists to
 * stop one being silently mis-read the day somebody configures one.
 */
function weekTypeFor(tz: string, at: Date): string {
  const iso = new Date(
    new Date(at.toLocaleString('en-US', { timeZone: tz })).toDateString(),
  );
  const target = new Date(iso);
  // Thursday of this week decides the ISO week number.
  target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7));
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  firstThursday.setDate(firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7));
  const week = 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
  return week % 2 === 0 ? '0' : '1';
}

/**
 * Today's productive window for a calendar, and how much of it has passed.
 *
 * A null calendar means Odoo has none set, which it documents as "fully
 * flexible working hours" — there is no shift to be ahead or behind of, so this
 * reports an unscheduled day and the caller shows a count with no pace.
 */
export function paceWindow(cal: CalendarVM | null | undefined, at: Date = new Date()): PaceWindow {
  if (!cal || cal.spans.length === 0) return EMPTY_WINDOW;
  const tz = cal.tz || 'Australia/Sydney';
  const { minutes } = zonedNow(tz, at);
  return windowAt(cal, minutes, at);
}

/**
 * The same window measured to an arbitrary minute of today rather than to now.
 *
 * The leader board's hour bars need the pace line at the end of every hour, and
 * asking "how much of the day was worked by 10am" is the same question as "how
 * much has been worked" with a different clock reading — so it is the same sum.
 */
export function windowAt(
  cal: CalendarVM | null | undefined,
  minuteOfDay: number,
  at: Date = new Date(),
): PaceWindow {
  if (!cal || cal.spans.length === 0) return EMPTY_WINDOW;
  const merged = todaysSpans(cal, at);
  if (merged.length === 0) return EMPTY_WINDOW;

  let totalMinutes = 0;
  let elapsedMinutes = 0;
  let onShift = false;
  for (const span of merged) {
    totalMinutes += span.to - span.from;
    elapsedMinutes += Math.max(0, Math.min(minuteOfDay, span.to) - span.from);
    if (minuteOfDay >= span.from && minuteOfDay < span.to) onShift = true;
  }

  return {
    totalMinutes,
    elapsedMinutes,
    scheduled: totalMinutes > 0,
    onShift,
    fraction: totalMinutes > 0 ? Math.min(1, elapsedMinutes / totalMinutes) : 0,
  };
}

const EMPTY_WINDOW: PaceWindow = {
  totalMinutes: 0,
  elapsedMinutes: 0,
  scheduled: false,
  onShift: false,
  fraction: 0,
};

/**
 * Today's productive spans for a calendar, in minutes from midnight, merged.
 *
 * Two attendance rows covering the same hour is a configuration accident rather
 * than two hours of work, and touching rows — the 13:00–14:15 and 14:15–17:45
 * pair on the two-shift calendar — are one span, not two.
 */
function todaysSpans(cal: CalendarVM, at: Date): Array<{ from: number; to: number }> {
  const tz = cal.tz || 'Australia/Sydney';
  const { dayOfWeek } = zonedNow(tz, at);
  const weekType = cal.twoWeeks ? weekTypeFor(tz, at) : null;

  const today = cal.spans
    .filter((s) => s.dayofweek === dayOfWeek)
    .filter((s) => (weekType == null ? true : s.weekType == null || s.weekType === weekType))
    .sort((a, b) => a.from - b.from);

  const merged: Array<{ from: number; to: number }> = [];
  for (const span of today) {
    const from = Math.round(clampHour(span.from) * 60);
    const to = Math.round(clampHour(span.to) * 60);
    if (to <= from) continue;
    const last = merged[merged.length - 1];
    if (last && from <= last.to) last.to = Math.max(last.to, to);
    else merged.push({ from, to });
  }
  return merged;
}

/** Odoo stores hours as floats; anything outside a day is a bad row. */
function clampHour(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(24, v));
}

/**
 * How many of a day's target should be done by now.
 *
 * Floored, not rounded: pods are whole, and at 51% of the day against a target
 * of 11 the pace line is 5.61 — five pods are owed and the sixth is not owed
 * yet. Rounding up to 6 there called an operator holding five "behind", which
 * is the whole complaint: nobody is behind on a pod they are still inside the
 * time for. Flooring makes one number serve the chip, the bar's colour and the
 * marker drawn on it — on pace is `done >= dueNow`, and it is the same
 * comparison everywhere.
 *
 * Null when there is no target to pro-rata, or when the calendar does not work
 * today — a Sunday has no pace to be behind.
 */
export function dueByNow(target: number | null | undefined, window: PaceWindow): number | null {
  if (target == null || target <= 0) return null;
  if (!window.scheduled) return null;
  return Math.floor(target * window.fraction);
}

/**
 * The tick bar under the score: one segment per unit of target, marking what is
 * done, where pace says you should be, and what is still to go.
 *
 * The marker is drawn OVER the tick it lands on rather than behind it, which is
 * what the mock shows and is the only reading that survives contact: at 6 of 11
 * with 5 due, frame 3b draws five green, one black and five grey — the marker
 * standing where the fifth pod was owed, with the sixth underneath it. Hiding
 * the marker behind a done tick would lose the one thing the bar is for the
 * moment an operator got ahead, which is exactly when they want to see it.
 *
 * No marker once the day's target is met: it would then only ever point at the
 * end of a bar that is already full.
 *
 * A long target would draw a bar of slivers, so it caps — past the cap the bar
 * stops being a count and becomes a proportion, which is what it was always
 * being read as anyway.
 */
export function tickBar(
  done: number,
  target: number | null,
  dueNow: number | null,
  max = 11,
): Array<'done' | 'now' | 'todo'> {
  if (target == null || target <= 0) return [];
  const n = Math.min(target, max);
  const scale = (v: number) => Math.round((v / target) * n);
  const doneTicks = Math.max(0, Math.min(n, scale(done)));
  const nowTick = dueNow == null || done >= target ? -1 : Math.max(0, Math.min(n - 1, scale(dueNow)));
  return Array.from({ length: n }, (_, i) =>
    i === nowTick ? 'now' : i < doneTicks ? 'done' : 'todo',
  );
}

/** Start of the current local day in the app's zone, as an Odoo datetime (UTC). */
export function localMidnightUtc(tz = 'Australia/Sydney', at: Date = new Date()): string {
  return dayStartUtc(localDateKey(tz, at), tz);
}

/**
 * How far the zone is ahead of UTC at a given instant, in ms.
 *
 * Assembled from `formatToParts` rather than reparsing a formatted string,
 * because reparsing measures the gap between the zone and *the device's* zone.
 * That reads as zero on a tablet already set to Sydney and as ten hours on a
 * CI box in UTC, and the answer here has to be the same on both.
 */
function zoneOffsetMs(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  // en-CA writes midnight as hour 24 of the day before it belongs to.
  const wallAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
  return wallAsUtc - at.getTime();
}

/**
 * Midnight at the start of a named local day, as an Odoo datetime (UTC).
 *
 * The offset is applied twice on purpose. The first pass has to guess the zone
 * offset from the wrong instant — the key read as if it were UTC — which is off
 * by an hour on the two days a year the clocks move. The second pass asks the
 * zone about the instant the first pass actually landed on, which is on the
 * correct side of the changeover, so it settles there.
 */
export function dayStartUtc(dateKey: string, tz = 'Australia/Sydney'): string {
  const naive = Date.parse(`${dateKey}T00:00:00Z`);
  const once = naive - zoneOffsetMs(tz, new Date(naive));
  const settled = naive - zoneOffsetMs(tz, new Date(once));
  return new Date(settled).toISOString().slice(0, 19).replace('T', ' ');
}

/**
 * The day `days` either side of a date key.
 *
 * Calendar arithmetic on the key itself, in UTC, so it never picks up an hour
 * from a daylight-saving change: 2026-04-05 plus one day is 2026-04-06 whether
 * or not that particular night was 25 hours long.
 */
export function shiftDateKey(dateKey: string, days: number): string {
  const d = new Date(`${dateKey}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * An Odoo datetime — naive UTC, "2026-09-22 03:03:11" — as a wall-clock time
 * in the factory's zone: "13:03". Empty for anything unparseable.
 */
export function fmtLocalTime(odooDatetime: string | null | undefined, tz = 'Australia/Sydney'): string {
  if (!odooDatetime) return '';
  const at = Date.parse(odooDatetime.replace(' ', 'T') + 'Z');
  if (Number.isNaN(at)) return '';
  const { minutes } = zonedNow(tz, new Date(at));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Today's date in the app's zone, as Odoo writes a date field. */
export function localDateKey(tz = 'Australia/Sydney', at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Close-of-day projection: what the current run rate finishes by knock-off.
 *
 * Only claimed once enough of the day has gone for a rate to mean anything —
 * two pods in the first ten minutes projects a heroic day that never happens,
 * and a leader acting on that is worse off than one told nothing.
 */
export function projectClose(done: number, window: PaceWindow): number | null {
  if (!window.scheduled || window.fraction < 0.15) return null;
  return Math.round(done / window.fraction);
}

/** The mock's chip wording. Zero is "on pace", never "+0". */
export function paceLabel(delta: number | null): string {
  if (delta == null) return '—';
  if (delta === 0) return 'on pace';
  return delta > 0 ? `+${delta}` : `${delta}`;
}

/** Which state colour a pace figure carries. */
export function paceTone(delta: number | null): 'pos' | 'danger' | 'neutral' {
  if (delta == null || delta === 0) return 'neutral';
  return delta > 0 ? 'pos' : 'danger';
}

export { DAY_MINUTES };
